import { Box, Stack, Typography, styled, Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import { t } from "i18next";
import Chart, { useChart } from "src/components/chart";
import { formatCurrency } from "src/utils/format-currency";
import { formatNumber } from "src/utils/format-number";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

export default function ChartSearchesSpend({ monthlyData, currency, lang }: { monthlyData: any[], currency: string, lang: string }) {
    const categories = monthlyData.map(item => item.month);
    const total_spend = monthlyData.map(item => item.total_spend || 0);
    const organic_volume = monthlyData.map(item => item.organic_volume || 0);
    const paid_volume = monthlyData.map(item => item.paid_volume || 0);

    const chartOptions = useChart({
        chart: {
            type: 'line',
            toolbar: { show: false },
            stacked: true,
        },
        stroke: { width: [2, 0, 0], curve: 'smooth' },
        plotOptions: {
            bar: { columnWidth: '20%' },
        },
        fill: { type: ['solid', 'solid', 'solid'] },
        colors: ['rgba(192, 153, 16, 1)', 'rgba(24, 161, 85, 1)', '#C09910'],
        xaxis: {
            tooltip: { enabled: false },
            type: 'category',
            categories,
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: [
            { show: false, min: 0, labels: { formatter: (v: number) => formatCurrency(v, currency, lang, true, 1), } },
            { opposite: true, show: false, labels: { formatter: (v: number) => formatNumber(v) } },
        ],
        grid: { show: false },
        tooltip: {
            shared: true,
            intersect: false,
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const item = monthlyData[dataPointIndex] || { organic_volume: 0, paid_volume: 0, total_spend: 0 };

                // Créer un tableau des séries
                const tooltipData = [
                    { label: t("dashboard.global.brandPulse.organic"), value: item.organic_volume, color: 'rgba(24, 161, 85, 1)' },
                    { label: t("dashboard.global.brandPulse.paid"), value: item.paid_volume, color: 'rgba(192, 153, 16, 1)' },
                ];

                // Extraire spend estimate
                const spendSeries = tooltipData.filter((d: any) => d.isLine);
                const otherSeries = tooltipData.filter((d: any) => !d.isLine);

                // Trier les autres séries par valeur descendante
                otherSeries.sort((a, b) => b.value - a.value);

                // Recomposer tableau avec spend estimate à la fin
                const sortedTooltipData = [...otherSeries, ...spendSeries];

                // Générer HTML
                return `
            <div class="visibility-chart-tooltip" style="width: 250px; font-family: Arial, sans-serif;">
                <div class="visibility-chart-tooltip-header" style="margin-bottom: 8px; font-weight: bold;">
                    <span class="visibility-chart-tooltip-title">${t("dashboard.global.brandPulse.search_and_spend")}</span>
                    <span class="visibility-chart-tooltip-date" style="float: right;">${item.month}</span>
                </div>
                ${sortedTooltipData.reverse().map((d: any) => d.isLine ? `
                    <div class="visibility-chart-tooltip-row" style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <line x1="1" y1="7" x2="13" y2="7" stroke="${d.color}" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        <span class="visibility-chart-tooltip-label" style="margin-left: 7px">${d.label}</span>
                        <span style="color: rgba(51, 60, 71, 1)">${formatCurrency(d.value, currency, lang, true, 1)}</span>
                    </div>
                ` : `
                    <div class="visibility-chart-tooltip-row" style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                        <div class="visibility-chart-tooltip-indicator" style="background-color: ${d.color}"></div>
                        <span class="visibility-chart-tooltip-label">${d.label}</span>
                        <span style="color: rgba(51, 60, 71, 1)">${formatNumber(d.value)}</span>
                    </div>
                `).join('')}

                   <div class="visibility-chart-tooltip-row" style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <line x1="1" y1="7" x2="13" y2="7" stroke="#C09910" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        <span class="visibility-chart-tooltip-label" style="margin-left: 7px">${t("dashboard.global.brandPulse.spend_estimate")}</span>
                        <span style="color: rgba(51, 60, 71, 1)">${formatCurrency(item.total_spend, currency, lang, true, 1)}</span>
                    </div>
            </div>
        `;
            },
        },
        legend: { show: false },
    });

    const series = [
        { name: "Spend estimate", type: 'line', data: total_spend },
        { name: "Organic volume", type: 'bar', data: organic_volume },
        { name: "Paid volume", type: 'bar', data: paid_volume },
    ];

    const renderFooter = (title: string) => {
        return (
            <Stack className="card-footer" width="100%" sx={{ borderBottomLeftRadius: '8px', borderBottomRightRadius: "8px" }}>
                <Typography fontSize={"13px"} className="card-footer-text" variant="caption" color="rgba(99, 115, 129, 1)" textAlign="center">
                    {title}
                </Typography>
            </Stack>
        );
    };

    return (
        <Box bgcolor="white" borderRadius={1}>
            <Stack className="card-top-header" borderBottom={1} borderColor="divider" direction="row" spacing={3} py={1} px={2} justifyContent="space-between">
                <LightTooltip title={"Lorem ipsum dolor col..."}>
                    <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1} sx={{ cursor: 'help' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography className="card-top-header-item-label" variant="h6" color="text.primary">
                                {t("dashboard.global.brandPulse.searches_split")}
                            </Typography>
                        </Stack>
                        <Typography className="card-top-header-item-value" variant="h6" color="text.primary">
                            {formatNumber(organic_volume.reduce((a, b) => a + b, 0) + paid_volume.reduce((a, b) => a + b, 0))}
                        </Typography>
                    </Stack>
                </LightTooltip>
                <LightTooltip title={"Lorem ipsum dolor col..."}>
                    <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1} sx={{ cursor: 'help' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <line x1="1" y1="7" x2="13" y2="7" stroke="#C09910" stroke-width="2" stroke-linecap="round" />
                            </svg>
                            <Typography className="card-top-header-item-label" variant="h6" color="text.primary">
                                {t("dashboard.global.brandPulse.spend_estimate")}
                            </Typography>
                        </Stack>
                        <Typography className="card-top-header-item-value" variant="h6" color="text.primary">
                            {formatCurrency(total_spend.reduce((acc, val) => acc + val, 0), currency, lang, true, 1)}

                        </Typography>
                    </Stack>
                </LightTooltip>
            </Stack>

            <Box height={200}>
                <Chart type="line" series={series} options={chartOptions} height="100%" />
            </Box>

            {renderFooter(t("dashboard.global.brandPulse.last12Months"))}
        </Box>
    );
}